import { PhoneFrame, StatusBar, AppHeader, CheckItem, VoiceWave, BottomNav } from "./PhoneFrame";
import { Bell, Mail, Users, CalendarDays, Send } from "lucide-react";

export const VoiceScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="GoDiGo" subtitle="Listening…" />
    <div className="px-5">
      <div className="rounded-2xl bg-[#1B4D3E] p-4 text-white">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-lime-300">You said</p>
        <p className="mt-1 text-[13px] leading-snug">"Remind me to call the dentist tomorrow at 9, and add groceries to Saturday."</p>
        <VoiceWave className="mt-3" />
      </div>
      <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">GoDiGo organized</p>
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-[12px] font-medium text-slate-800"><Bell size={13} className="text-emerald-600" /> Call dentist — Tue 9:00 AM</div>
          <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-[12px] font-medium text-slate-800"><CalendarDays size={13} className="text-emerald-600" /> Groceries — Sat, To-Dos</div>
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] text-slate-400">Done. Anything else?</p>
    </div>
    <BottomNav />
  </PhoneFrame>
);

export const TodayScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="Today" subtitle="Tuesday, June 9" />
    <div className="mx-4 mb-3 rounded-2xl bg-gradient-to-br from-[#1B4D3E] to-[#2E7D5B] p-4 text-white">
      <p className="text-[10px] uppercase tracking-widest text-lime-300">Up next</p>
      <p className="mt-1 font-heading text-[15px] font-bold">Team standup · 10:30 AM</p>
      <p className="text-[11px] text-emerald-100">Google Meet · 3 attendees</p>
    </div>
    <CheckItem done label="Send project brief to Maya" meta="Completed 8:45 AM" />
    <CheckItem label="Review Q3 budget" meta="Due today · 2:00 PM" accent="High" />
    <CheckItem label="Pick up dry cleaning" meta="Reminder · 5:30 PM" />
    <CheckItem label="Book flight to Denver" meta="This week" />
    <BottomNav />
  </PhoneFrame>
);

export const TasksScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="Tasks" subtitle="6 open · 12 done this week" />
    <div className="mx-4 mb-3 flex gap-2">
      {["All", "Work", "Personal", "Errands"].map((t, i) => (
        <span key={t} className={`rounded-full px-3 py-1 text-[10px] font-semibold ${i === 0 ? "bg-[#1B4D3E] text-white" : "bg-slate-100 text-slate-500"}`}>{t}</span>
      ))}
    </div>
    <CheckItem label="Finish onboarding deck" meta="Work · Due Thu" accent="High" />
    <CheckItem label="Renew car insurance" meta="Personal · Due Fri" />
    <CheckItem done label="Email contractor quotes" meta="Done via Gmail" />
    <CheckItem label="Plan mom's birthday dinner" meta="Personal · Next week" />
    <CheckItem done label="Submit expense report" meta="Done yesterday" />
    <BottomNav />
  </PhoneFrame>
);

export const CalendarScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="Calendar" subtitle="June 2026" />
    <div className="mx-4 mb-3 grid grid-cols-7 gap-1 text-center text-[10px] text-slate-400">
      {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <span key={i}>{d}</span>)}
      {Array.from({ length: 14 }).map((_, i) => (
        <span key={i} className={`rounded-lg py-1 text-[11px] font-medium ${i === 8 ? "bg-[#1B4D3E] text-white" : i === 10 ? "bg-lime-200 text-emerald-900" : "text-slate-600"}`}>{i + 1}</span>
      ))}
    </div>
    {[
      { t: "Dentist appointment", m: "9:00 – 9:45 AM", c: "bg-emerald-500" },
      { t: "Team standup", m: "10:30 AM · Google Meet", c: "bg-lime-400" },
      { t: "Lunch with Sarah", m: "12:30 PM · Café Verde", c: "bg-teal-400" },
      { t: "Parent–teacher meeting", m: "4:00 PM", c: "bg-emerald-700" },
    ].map((e) => (
      <div key={e.t} className="mx-4 mb-2 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-3.5 py-2.5 shadow-sm">
        <span className={`h-8 w-1 rounded-full ${e.c}`} />
        <div>
          <p className="text-[12px] font-semibold text-slate-800">{e.t}</p>
          <p className="text-[10px] text-slate-400">{e.m}</p>
        </div>
      </div>
    ))}
    <BottomNav />
  </PhoneFrame>
);

export const RemindersScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="Reminders" subtitle="Never miss a thing" />
    {[
      { t: "Take medication", m: "Daily · 8:00 AM", on: true },
      { t: "Call dentist", m: "Tomorrow · 9:00 AM", on: true },
      { t: "Water the plants", m: "Every 3 days", on: true },
      { t: "Pay rent", m: "Monthly · 1st", on: false },
      { t: "Gym session", m: "Mon · Wed · Fri, 6:30 PM", on: true },
    ].map((r) => (
      <div key={r.t} className="mx-4 mb-2 flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-3.5 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"><Bell size={14} /></span>
          <div>
            <p className="text-[12px] font-semibold text-slate-800">{r.t}</p>
            <p className="text-[10px] text-slate-400">{r.m}</p>
          </div>
        </div>
        <span className={`flex h-5 w-9 items-center rounded-full p-0.5 ${r.on ? "justify-end bg-emerald-600" : "justify-start bg-slate-200"}`}>
          <span className="h-4 w-4 rounded-full bg-white shadow" />
        </span>
      </div>
    ))}
    <BottomNav />
  </PhoneFrame>
);

export const AssignScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="Assign" subtitle="Share the load" />
    <div className="mx-4 mb-3 rounded-2xl bg-[#1B4D3E] p-4 text-white">
      <p className="text-[10px] uppercase tracking-widest text-lime-300">New assignment</p>
      <p className="mt-1 text-[13px] font-semibold">Pick up the kids from practice</p>
      <div className="mt-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-lime-300 text-[9px] font-bold text-emerald-900">AM</span>
        <span className="text-[11px] text-emerald-100">Assigned to Alex · Today 5:00 PM</span>
        <Send size={12} className="ml-auto text-lime-300" />
      </div>
    </div>
    {[
      { t: "Book venue for offsite", w: "Maya R.", s: "In progress", c: "bg-amber-100 text-amber-700" },
      { t: "Send invoices to clients", w: "Jordan K.", s: "Done", c: "bg-emerald-100 text-emerald-700" },
      { t: "Order office supplies", w: "Sam T.", s: "Pending", c: "bg-slate-100 text-slate-500" },
    ].map((a) => (
      <div key={a.t} className="mx-4 mb-2 flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-3.5 py-3 shadow-sm">
        <div>
          <p className="text-[12px] font-semibold text-slate-800">{a.t}</p>
          <p className="text-[10px] text-slate-400">{a.w}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${a.c}`}>{a.s}</span>
      </div>
    ))}
    <BottomNav />
  </PhoneFrame>
);

export const IntegrationsScreen = () => (
  <PhoneFrame>
    <StatusBar />
    <AppHeader title="Connections" subtitle="You control every permission" />
    {[
      { icon: <Mail size={16} />, t: "Gmail", m: "Turn emails into tasks", on: true },
      { icon: <CalendarDays size={16} />, t: "Google Calendar", m: "Two-way event sync", on: true },
      { icon: <Users size={16} />, t: "Google Contacts", m: "Assign & reach people fast", on: true },
      { icon: <Bell size={16} />, t: "Device notifications", m: "Alarms & nudges", on: true },
    ].map((x) => (
      <div key={x.t} className="mx-4 mb-2 flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-3.5 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">{x.icon}</span>
          <div>
            <p className="text-[12px] font-semibold text-slate-800">{x.t}</p>
            <p className="text-[10px] text-slate-400">{x.m}</p>
          </div>
        </div>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold text-emerald-700">Authorized</span>
      </div>
    ))}
    <div className="mx-4 mt-3 rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-3 text-center">
      <p className="text-[10px] leading-relaxed text-emerald-800">GoDiGo only accesses what you explicitly authorize. Revoke anytime.</p>
    </div>
    <BottomNav />
  </PhoneFrame>
);
